import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { ReplaySubject, combineLatest, map } from 'rxjs';

import { getAvailabilityLevel } from 'src/app/shared/availability-level';

import { ColorSchemeService } from '../../../color-scheme.service';
import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { overviewTimelineFeature } from '../../../job/job-overview/overview-timeline/overview-timeline.feature';

import { AvailabilityIconComponent } from './availability-icon.component';

@Component({
    selector: 'app-availability-date',
    standalone: true,
    imports: [FreyaCommonModule, AvailabilityIconComponent],
    templateUrl: './availability-date.component.html',
    styleUrls: [
      './availability-date.component.scss',
      '../../../job/colors.scss',
    ],
})
export class AvailabilityDateComponent {

    private _date$ = new ReplaySubject<string>(1);

    @Input() set date(value: string) {
        this._date$.next(value);
    }

    /** Whether to show the pricing tier pill badge. Defaults true (new opportunity).
     *  Set false in the calculator to keep the tier hidden / mysterious. */
    @Input() showTier = true;

    viewModel$ = combineLatest([
      this._date$,
      this.store.select(overviewTimelineFeature.selectViewDatesMap),
    ]).pipe(
        map(([ dateStr, viewDatesMap ]) => {
          const dayData = viewDatesMap[dateStr] as any;

          if (!dayData) return null;

          const trucksAvailable = Math.max(0, dayData.truckCapacity - dayData.actualTrucksUsed);
          const crewRequired = Math.max(dayData.actualCrewUsed, dayData.actualCrewRequired);
          const crewAvailable = Math.max(0, dayData.crewCapacity - crewRequired);

          // Pricing tier from prophet forecast (null when no forecast available)
          const tier: { name: string; percent: number; color?: string | null } | null =
              dayData.prophet?.discount?.tier ?? null;

          return {
            date: dateStr,
            formattedDate: dayjs(dateStr).format('ddd, MMM D'),
            trucksAvailable,
            trucksTotal: dayData.truckCapacity,
            colorClass: `availability-${getAvailabilityLevel(trucksAvailable, dayData.truckCapacity)}`,
            crewAvailable,
            crewTotal: dayData.crewCapacity,
            tier,
            remark: (dayData.remark?.text ?? '').trim()
              ? {
                ...dayData.remark,
                text: dayData.remark.text.trim(),
                severity: dayData.remark.severity || 'info',
              }
              : null,
          };

        }),
    );

    constructor(
      private store: Store,
      public colorScheme: ColorSchemeService,
    ) {}

}
